<template>
  <div class="pdf-js-viewer">
    <div
      v-if="!hideNativePreviewOption"
      class="pdf-js-viewer__switch"
      role="tablist"
      aria-label="PDF 预览方式"
    >
      <button
        type="button"
        role="tab"
        :aria-selected="view === 'canvas'"
        :class="{ 'pdf-js-viewer__tab--active': view === 'canvas' }"
        class="pdf-js-viewer__tab"
        @click="setView('canvas')"
      >
        页面内渲染
      </button>
      <button
        type="button"
        role="tab"
        :aria-selected="view === 'native'"
        :class="{ 'pdf-js-viewer__tab--active': view === 'native' }"
        class="pdf-js-viewer__tab"
        @click="setView('native')"
      >
        系统预览（可复制）
      </button>
    </div>

    <p v-if="view === 'canvas' && errorMsg" class="pdf-js-viewer__error">{{ errorMsg }}</p>

    <!-- 须用 v-if：v-show 仍会加载 iframe 内 PDF，iOS/部分浏览器会误触发下载 -->
    <iframe
      v-if="view === 'native'"
      :key="pdfSrc"
      :src="pdfSrc"
      title="PDF"
      class="pdf-js-viewer__iframe"
    />

    <div
      v-show="view === 'canvas'"
      ref="pagesRoot"
      v-loading="loading"
      class="pdf-js-viewer__pages"
      element-loading-text="正在加载 PDF…"
    />
  </div>
</template>

<script>
import { markRaw, toRaw } from 'vue'
import { TextLayer } from 'pdfjs-dist'
import * as pdfjsLib from 'pdfjs-dist'
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url'

/** 升级 key 后不再沿用旧版里误存的「系统预览」，默认回到页面内渲染 */
const STORAGE_KEY = 'pdf-js-viewer:view:v2'

/** Webpack ?url 可能得到 string，或 { default: string }，pdf.js 4 要求 workerSrc 必须是 string */
function resolvePdfWorkerSrc(m) {
  if (typeof m === 'string' && m) return m
  if (m != null && typeof m === 'object' && typeof m.default === 'string' && m.default) {
    return m.default
  }
  return ''
}

const PDFJS_DIST_VER = '4.10.38'
const workerSrcResolved =
  resolvePdfWorkerSrc(pdfWorkerUrl) ||
  `https://unpkg.com/pdfjs-dist@${PDFJS_DIST_VER}/build/pdf.worker.mjs`

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrcResolved

/** 手机/窄屏：禁用 iframe 内嵌 PDF（易弹下载），仅用 PDF.js + 可选 fetch 拉二进制 */
function shouldForceCanvasOnlyPreview() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) {
    return true
  }
  try {
    if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) {
      return true
    }
  } catch (e) {
    /* ignore */
  }
  return false
}

export default {
  name: 'PdfJsViewer',
  props: {
    pdfSrc: {
      type: String,
      required: true,
    },
    /** 无本地记录时的默认模式：native=系统 iframe；canvas=页面内 PDF.js */
    defaultView: {
      type: String,
      default: 'canvas',
      validator: (v) => v === 'native' || v === 'canvas',
    },
  },
  data() {
    return {
      view: 'canvas',
      loading: false,
      errorMsg: '',
      loadGen: 0,
      /** 每次进入 paintPages 递增，用于取消重叠的 resize / 重入绘制 */
      paintRunSeq: 0,
      resizeObs: null,
      roTimer: null,
      pdfLoadingTask: null,
      pdfDocument: null,
      cachedPdfSrc: '',
      lastPaintedWidth: 0,
      hideNativePreviewOption: false,
    }
  },
  watch: {
    pdfSrc: {
      handler() {
        if (this.view !== 'canvas') return
        // 先作废进行中的 paintPages，再 destroy，避免 page.render 过程中抛 Rendering cancelled
        this.loadGen += 1
        this.lastPaintedWidth = 0
        this.cachedPdfSrc = ''
        this._safeDisposePdfDocument()
        this.$nextTick(() => this.renderDoc())
      },
    },
  },
  mounted() {
    const forceCanvas = shouldForceCanvasOnlyPreview()
    this.hideNativePreviewOption = forceCanvas
    let initial = this.defaultView
    if (!forceCanvas) {
      try {
        const s = localStorage.getItem(STORAGE_KEY)
        if (s === 'native' || s === 'canvas') {
          initial = s
        }
      } catch (e) {
        initial = this.defaultView
      }
    } else {
      initial = 'canvas'
    }
    this.view = initial
    if (this.view === 'canvas') {
      this.$nextTick(() => {
        this.bindResize()
        this.renderDoc()
      })
    }
  },
  beforeUnmount() {
    this.loadGen += 1
    this.unbindResize()
    this._safeDestroyLoadingTask()
    this._safeDisposePdfDocument()
  },
  methods: {
    /**
     * PDF.js 4 的 PDFDocumentProxy 使用 # 私有字段；若经 Vue 的 Proxy 调用 destroy 会报
     * "Cannot read private member #passwordCapability ..."。必须先 toRaw。
     */
    _safeDisposePdfDocument() {
      const doc = this.pdfDocument
      this.pdfDocument = null
      if (!doc) return
      try {
        const raw = toRaw(doc)
        if (raw && typeof raw.destroy === 'function') {
          raw.destroy()
        }
      } catch (e) {
        /* ignore */
      }
    },
    _safeDestroyLoadingTask() {
      const task = this.pdfLoadingTask
      this.pdfLoadingTask = null
      if (!task) return
      try {
        const raw = toRaw(task)
        if (raw && typeof raw.destroy === 'function') {
          raw.destroy()
        }
      } catch (e) {
        /* ignore */
      }
    },
    /** PDF.js 在切换文档或取消渲染时抛出的可忽略错误 */
    _isPdfRenderCancelledError(err) {
      if (!err) return false
      const name = String(err.name || '')
      const msg = String(err.message || err)
      return (
        name === 'RenderingCancelledException' ||
        name === 'AbortException' ||
        /Rendering cancelled/i.test(msg) ||
        /Worker was destroyed/i.test(msg)
      )
    },
    setView(v) {
      if (this.hideNativePreviewOption && v === 'native') {
        return
      }
      if (this.view === v) return
      if (v === 'native') {
        this.loadGen += 1
        this.unbindResize()
        this._safeDisposePdfDocument()
        this._safeDestroyLoadingTask()
        const root = this.$refs.pagesRoot
        if (root) root.innerHTML = ''
        this.lastPaintedWidth = 0
        this.cachedPdfSrc = ''
        this.errorMsg = ''
      }
      this.view = v
      try {
        localStorage.setItem(STORAGE_KEY, v)
      } catch (e) {
        /* ignore */
      }
      if (v === 'canvas') {
        this.$nextTick(() => {
          this.bindResize()
          this.renderDoc()
        })
      }
    },
    bindResize() {
      this.unbindResize()
      const root = this.$refs.pagesRoot
      if (typeof ResizeObserver === 'undefined' || !root) return
      this.resizeObs = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry || !this.pdfDocument) return
        const w = Math.round(entry.contentRect.width)
        if (w < 80) return
        if (
          this.lastPaintedWidth > 0 &&
          Math.abs(w - this.lastPaintedWidth) < 2
        ) {
          return
        }
        if (this.roTimer) clearTimeout(this.roTimer)
        this.roTimer = setTimeout(() => {
          const cw = Math.round(this.containerWidth())
          if (
            this.lastPaintedWidth > 0 &&
            Math.abs(cw - this.lastPaintedWidth) < 2
          ) {
            return
          }
          this.renderDoc({ repaintOnly: true })
        }, 280)
      })
      const target = root.parentElement || root
      this.resizeObs.observe(target)
    },
    unbindResize() {
      if (this.resizeObs) {
        this.resizeObs.disconnect()
        this.resizeObs = null
      }
      if (this.roTimer) {
        clearTimeout(this.roTimer)
        this.roTimer = null
      }
    },
    containerWidth() {
      const el = this.$refs.pagesRoot
      if (!el) return 800
      const w = el.parentElement ? el.parentElement.clientWidth : el.clientWidth
      return Math.max(280, w || 800)
    },
    async renderDoc(options = {}) {
      if (this.view !== 'canvas') return
      const repaintOnly = options.repaintOnly === true
      if (repaintOnly && !this.pdfDocument) return
      const gen = repaintOnly ? this.loadGen : ++this.loadGen
      const url = (this.pdfSrc || '').trim()
      const root = this.$refs.pagesRoot

      if (!url) {
        this.errorMsg = '无效的 PDF 地址'
        this.loading = false
        return
      }

      if (!repaintOnly) {
        this._safeDestroyLoadingTask()
      }

      if (repaintOnly && this.pdfDocument && this.cachedPdfSrc === url) {
        this.errorMsg = ''
        // 与新一轮全量加载一样递增，避免两次 repaintOnly 并发 paintPages
        this.loadGen += 1
        const g2 = this.loadGen
        await this.paintPages(this.pdfDocument, g2)
        return
      }

      this.loading = true
      this.errorMsg = ''
      if (root) root.innerHTML = ''

      try {
        const useFetchBinary =
          this.hideNativePreviewOption ||
          shouldForceCanvasOnlyPreview()
        if (useFetchBinary) {
          const res = await fetch(url, { credentials: 'include', mode: 'cors' })
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`)
          }
          const buf = await res.arrayBuffer()
          this.pdfLoadingTask = pdfjsLib.getDocument({ data: buf })
        } else {
          this.pdfLoadingTask = pdfjsLib.getDocument({
            url,
            withCredentials: true,
          })
        }
        const pdf = await this.pdfLoadingTask.promise
        this.pdfLoadingTask = null
        if (gen !== this.loadGen) return
        this.pdfDocument = markRaw(pdf)
        this.cachedPdfSrc = url
        await this.paintPages(this.pdfDocument, gen)
      } catch (e) {
        if (gen !== this.loadGen) return
        if (this._isPdfRenderCancelledError(e)) return
        this.errorMsg =
          'PDF 预览失败（可能受跨域或登录限制）。请使用「新窗口打开」查看。'
      } finally {
        if (gen === this.loadGen) this.loading = false
      }
    },
    _paintStale(gen, myRun) {
      return gen !== this.loadGen || myRun !== this.paintRunSeq
    },
    async paintPages(pdf, gen) {
      const myRun = ++this.paintRunSeq
      const root = this.$refs.pagesRoot
      if (!root || this._paintStale(gen, myRun)) return
      root.innerHTML = ''

      const pdfDoc = toRaw(pdf) || pdf
      if (!pdfDoc || typeof pdfDoc.getPage !== 'function') return

      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const containerW = this.containerWidth()
      const numPages = pdfDoc.numPages

      for (let i = 1; i <= numPages; i++) {
        if (this._paintStale(gen, myRun)) return
        let page
        try {
          page = await pdfDoc.getPage(i)
        } catch (e) {
          if (this._paintStale(gen, myRun) || this._isPdfRenderCancelledError(e)) return
          throw e
        }
        if (this._paintStale(gen, myRun)) return
        const vp1 = page.getViewport({ scale: 1 })
        const cssScale = Math.max(0.08, (containerW - 8) / vp1.width)
        const viewport = page.getViewport({ scale: cssScale })
        const transform =
          dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null

        const layerHost = document.createElement('div')
        layerHost.className = 'pdf-js-viewer__layerHost'
        layerHost.style.position = 'relative'
        layerHost.style.margin = '0 auto'
        layerHost.style.width = `${viewport.width}px`
        layerHost.style.height = `${viewport.height}px`
        layerHost.style.setProperty('--scale-factor', String(viewport.scale))

        const canvasWrap = document.createElement('div')
        canvasWrap.className = 'pdf-js-viewer__canvasWrap'

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d', { alpha: false })
        if (!ctx) continue

        canvas.width = Math.floor(viewport.width * dpr)
        canvas.height = Math.floor(viewport.height * dpr)
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`

        canvasWrap.appendChild(canvas)

        const textLayerDiv = document.createElement('div')
        textLayerDiv.className = 'textLayer'
        textLayerDiv.setAttribute('tabindex', '0')

        layerHost.appendChild(canvasWrap)
        layerHost.appendChild(textLayerDiv)

        const wrap = document.createElement('div')
        wrap.className = 'pdf-js-viewer__page'
        wrap.appendChild(layerHost)
        root.appendChild(wrap)

        try {
          const renderTask = page.render({ canvasContext: ctx, viewport, transform })
          await renderTask.promise
        } catch (e) {
          if (this._paintStale(gen, myRun) || this._isPdfRenderCancelledError(e)) return
          throw e
        }

        if (this._paintStale(gen, myRun)) return

        try {
          const textLayer = new TextLayer({
            textContentSource: page.streamTextContent({
              includeMarkedContent: true,
              disableNormalization: true,
            }),
            container: textLayerDiv,
            viewport,
          })
          await textLayer.render()
        } catch (e) {
          if (this._paintStale(gen, myRun) || this._isPdfRenderCancelledError(e)) return
          throw e
        }

        const endOfContent = document.createElement('div')
        endOfContent.className = 'endOfContent'
        textLayerDiv.append(endOfContent)
        textLayerDiv.addEventListener('mousedown', () => {
          textLayerDiv.classList.add('selecting')
        })
      }
      if (!this._paintStale(gen, myRun)) {
        this.lastPaintedWidth = Math.round(containerW)
      }
    },
  },
}
</script>

<style scoped>
.pdf-js-viewer {
  width: 100%;
  min-height: 120px;
  background: #ffffff;
  user-select: text;
  -webkit-user-select: text;
}

.pdf-js-viewer__switch {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  justify-content: center;
}

.pdf-js-viewer__tab {
  padding: 6px 14px;
  font-size: 13px;
  border: 1px solid #c5ced9;
  border-radius: 8px;
  background: #f5f7fa;
  color: #303133;
  cursor: pointer;
}

.pdf-js-viewer__tab:hover {
  border-color: #175cc4;
  color: #175cc4;
}

.pdf-js-viewer__tab--active {
  border-color: #175cc4;
  background: #e8f0fc;
  color: #175cc4;
  font-weight: 600;
}

.pdf-js-viewer__iframe {
  width: 100%;
  min-height: min(72vh, 720px);
  border: none;
  display: block;
  background-color: #ffffff;
  color-scheme: only light;
  vertical-align: top;
}

.pdf-js-viewer__pages {
  background: #ffffff;
  text-align: center;
  min-height: 80px;
}

.pdf-js-viewer__page {
  margin: 0 auto 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  display: block;
  max-width: 100%;
}

.pdf-js-viewer__page canvas {
  display: block;
  margin: 0 auto;
  vertical-align: top;
  pointer-events: none;
}

.pdf-js-viewer__error {
  margin: 0;
  padding: 12px;
  font-size: 13px;
  color: #e6a23c;
  background: #fdf6ec;
  border-radius: 8px;
}

.pdf-js-viewer__canvasWrap {
  position: relative;
  z-index: 0;
  line-height: 0;
}
</style>

<style>
.pdf-js-viewer .textLayer {
  position: absolute;
  text-align: initial;
  inset: 0;
  overflow: clip;
  opacity: 1;
  line-height: 1;
  -webkit-text-size-adjust: none;
  text-size-adjust: none;
  forced-color-adjust: none;
  transform-origin: 0 0;
  caret-color: CanvasText;
  z-index: 1;
  pointer-events: auto;
}

.pdf-js-viewer .textLayer :is(span, br) {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  transform-origin: 0 0;
}

.pdf-js-viewer .textLayer ::-moz-selection {
  background: rgba(0, 0, 255, 0.25);
}

.pdf-js-viewer .textLayer ::selection {
  background: rgba(0, 0, 255, 0.25);
}

.pdf-js-viewer .textLayer .endOfContent {
  display: block;
  position: absolute;
  inset: 100% 0 0;
  z-index: 0;
  cursor: default;
  user-select: none;
}

.pdf-js-viewer .textLayer.selecting .endOfContent {
  top: 0;
}
</style>
